import { PersonArmsSpread, Eye, EarSlash, HandTap, TextAa, Keyboard, VideoCamera, Book } from "@phosphor-icons/react";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { useAccess } from "@/context/AccessContext";

const BUILT_IN = [
  [Eye, "If you use a screen reader",
   ["Every page has a heading order, landmarks and a skip-to-content link.",
    "Every button, icon and field has a name read out loud — nothing is left as a bare icon.",
    "Timers, break alerts and the emergency alarm are announced as they change, not just shown.",
    "Board pictures carry a written description."]],
  [EarSlash, "If you are deaf or hard of hearing",
   ["Nothing depends on sound. The stretch timer flashes the screen and vibrates the phone between moves.",
    "Break reminders and the emergency alarm show a full-width banner you cannot miss.",
    "Video posts can carry captions, and notices can carry a signed video.",
    "Turn 'Show alerts on screen' on and every audio cue becomes a visible one."]],
  [HandTap, "If using your hands is hard",
   ["Everything works from the keyboard with a clear focus ring you can see.",
    "Nothing is hidden behind a hover — if you can see it, you can tap it.",
    "Tap targets are at least 44 pixels, bigger on the phone.",
    "'Reduce motion' stops animations and transitions."]],
  [TextAa, "If the text is too small",
   ["Three text sizes, set from the accessibility button on any page.",
    "High contrast strengthens colours and thickens borders.",
    "Your choice stays on the device — you set it once."]],
];

export default function Accessibility() {
  const { prefs, setPref, announce } = useAccess();

  useSeo({
    title: "Accessibility — built for blind, deaf and disabled workers | Unity & Utility",
    description: "Screen reader support, visual alerts instead of sounds, full keyboard operation, three text sizes, high contrast, reduced motion, captions and signed video, plus easy-read versions of every board.",
    path: "/accessibility",
    jsonLd: breadcrumbs([["Home", "/"], ["Accessibility", "/accessibility"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-4xl px-5 py-10 sm:px-8 sm:py-14" data-testid="accessibility-page">
        <Crumbs items={[["Home", "/"], ["Accessibility", "/accessibility"]]} />
        <p className="overline text-[#B3241B]">Accessibility</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <PersonArmsSpread size={34} weight="duotone" className="mt-1 shrink-0 text-[#002FA7]" />
          Built to be used by everyone on the crew
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="accessibility-intro">
          Blind, deaf, hard of hearing, one-handed, using a keyboard only, or just needing bigger
          text — the work does not change, so the tools should not either. Here is what is built in
          and how to switch it on.
        </p>

        <section className="mt-10 border border-[#002FA7] bg-white p-5" data-testid="accessibility-settings">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Your settings
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            The same controls sit behind the accessibility button in the corner of every page.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[["normal", "Normal text"], ["large", "Large text"], ["xlarge", "Largest text"]].map(([value, label]) => (
              <button key={value} type="button" data-testid={`page-text-${value}`}
                aria-pressed={prefs.textSize === value}
                onClick={() => { setPref("textSize", value); announce(`Text size ${label}`); }}
                className={`tap-safe border px-4 py-2 text-sm font-semibold ${
                  prefs.textSize === value ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-700"}`}>
                {label}
              </button>
            ))}
            {[["contrast", "High contrast"], ["reduceMotion", "Reduce motion"],
              ["visualAlerts", "Alerts on screen"]].map(([key, label]) => (
              <button key={key} type="button" data-testid={`page-toggle-${key}`}
                aria-pressed={!!prefs[key]}
                onClick={() => { setPref(key, !prefs[key]); announce(`${label} ${!prefs[key] ? "on" : "off"}`); }}
                className={`tap-safe border px-4 py-2 text-sm font-semibold ${
                  prefs[key] ? "border-[#002FA7] bg-[#EEF3FF] text-[#002FA7]"
                    : "border-border bg-white text-slate-700"}`}>
                {label}: {prefs[key] ? "on" : "off"}
              </button>
            ))}
          </div>
        </section>

        <div className="mt-10 space-y-6">
          {BUILT_IN.map(([Icon, title, points], i) => (
            <section key={title} data-testid={`accessibility-group-${i}`} className="border border-border bg-white p-5">
              <h2 className="font-display flex items-center gap-2 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
                <Icon size={20} weight="duotone" className="text-[#002FA7]" /> {title}
              </h2>
              <ul className="mt-2 space-y-1 text-sm leading-relaxed text-slate-700">
                {points.map((p) => <li key={p}>· {p}</li>)}
              </ul>
            </section>
          ))}
        </div>

        <section className="mt-10 grid gap-4 sm:grid-cols-3">
          {[[Book, "Easy read", "Every daily board card has a short plain-language version: short sentences, no jargon."],
            [VideoCamera, "Signed video", "House notices and safety rules can carry a signed video for anyone who reads sign first."],
            [Keyboard, "Keyboard only", "Tab moves through everything in a sensible order with a focus ring you can see."]].map(([Icon, head, body]) => (
            <div key={head} className="border border-border bg-white p-4">
              <Icon size={20} weight="duotone" className="text-[#002FA7]" />
              <p className="mt-2 text-sm font-semibold text-[#0F172A]">{head}</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{body}</p>
            </div>
          ))}
        </section>

        <section className="mt-10" data-testid="accessibility-report">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Something in the way?
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            Tell us which page and what happened and it gets fixed — through the help desk inside the
            app, or by email to the address on the contact page. We aim to answer within two working
            days. We are working to WCAG 2.2 AA and will say plainly where we are not there yet.
          </p>
        </section>
      </div>
    </PublicShell>
  );
}
